import React, { useState, useEffect, useCallback } from 'react';
import { Menu, X, Phone, Mail, Instagram } from 'lucide-react';
import { Button } from './ui/button';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setIsScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setIsMobileMenuOpen(false);
    }
  };

  const menuItems = [
    { label: 'Start', id: 'hero' },
    { label: 'Leistungen', id: 'services' },
    { label: 'Über uns', id: 'about' },
    { label: 'Projekte', id: 'projects' },
    { label: 'Kontakt', id: 'contact' }
  ];

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'bg-white/95 backdrop-blur-md shadow-lg' 
          : 'bg-gradient-to-b from-white/90 to-white/70 backdrop-blur-sm'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo - Original THS BAUPROJEKTE GMBH Logo */}
          <div 
            className="flex-shrink-0 cursor-pointer group" 
            onClick={() => scrollToSection('hero')}
          >
            <img 
              src="https://customer-assets.emergentagent.com/job_dde5972f-5219-42ab-9192-5714e8a2e750/artifacts/zwvvm7v5_Black_and_White_Minimalist_Wedding_Monogram_Logo.pdf.png"
              alt="THS Bauprojekte GmbH Logo"
              className="h-20 sm:h-24 w-auto object-contain group-hover:scale-105 transition-transform duration-300"
            />
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="relative text-gray-800 hover:text-black font-medium text-sm uppercase tracking-wide transition-colors duration-300 group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-gradient-to-r from-black to-[#00CCDD] group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </nav>

          {/* Desktop Contact & CTA */}
          <div className="hidden lg:flex items-center space-x-4">
            <a 
              href="tel:017659885691" 
              className="flex items-center text-gray-800 hover:text-[#00CCDD] transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-black to-[#00CCDD] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Phone className="w-5 h-5 text-white" />
              </div>
              <span className="ml-2 font-medium text-sm hidden xl:block">0176 59885691</span>
            </a>
            <a
              href="https://www.instagram.com/ths.bauprojekte"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-gray-800 hover:text-[#00CCDD] transition-colors group"
              title="Aktuelle Projekte auf Instagram"
            >
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-black to-[#00CCDD] flex items-center justify-center group-hover:scale-110 transition-transform">
                <Instagram className="w-5 h-5 text-white" />
              </div>
            </a>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-black hover:bg-gradient-to-r hover:from-black hover:to-[#00CCDD] text-white px-6 py-2 font-semibold transition-all duration-300 hover:shadow-lg"
            >
              Kontakt
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-black" />
            ) : (
              <Menu className="w-6 h-6 text-black" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-xl animate-in slide-in-from-top duration-300">
          <nav className="container mx-auto px-4 py-6 flex flex-col space-y-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                className="text-left text-gray-800 hover:text-black font-medium py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors uppercase tracking-wide text-sm"
              >
                {item.label}
              </button>
            ))}
            <div className="pt-4 border-t">
              <a
                href="tel:017659885691"
                className="flex items-center text-gray-800 hover:text-[#00CCDD] py-3 px-4 transition-colors"
              >
                <Phone className="w-5 h-5 mr-3" />
                <span className="font-medium">0176 59885691</span>
              </a>
              <a
                href="mailto:info@thsbauprojekte.de"
                className="flex items-center text-gray-800 hover:text-[#00CCDD] py-3 px-4 transition-colors"
              >
                <Mail className="w-5 h-5 mr-3" />
                <span className="font-medium">info@thsbauprojekte.de</span>
              </a>
              <a
                href="https://www.instagram.com/ths.bauprojekte"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center text-gray-800 hover:text-[#00CCDD] py-3 px-4 transition-colors"
              >
                <Instagram className="w-5 h-5 mr-3" />
                <span className="font-medium">@ths.bauprojekte</span>
              </a>
            </div>
            <Button
              onClick={() => scrollToSection('contact')}
              className="bg-black hover:bg-gradient-to-r hover:from-black hover:to-[#00CCDD] text-white w-full py-4 font-semibold"
            >
              Jetzt Kontakt aufnehmen
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};